// pages/AdminLogin.js - Fixed Cherry Theme & Working Demo Login
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Lock, Mail, Shield, Flower, ArrowRight, Eye, EyeOff, User, Crown } from 'lucide-react';
import { toast } from 'react-toastify';

const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginType, setLoginType] = useState('admin');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const { loginWithPassword, isAdmin, isStaff, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Redirect if already logged in
  React.useEffect(() => {
    if (user) {
      if (isAdmin) {
        navigate('/admin', { replace: true });
      } else if (isStaff) {
        navigate('/staff', { replace: true });
      }
    }
  }, [user, isAdmin, isStaff, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('Please enter your email');
      return;
    }
    if (!password) {
      toast.error('Please enter your password');
      return;
    }

    setLoading(true);
    try {
      const result = await loginWithPassword(email, password);
      if (result) {
        toast.success('Login successful! Redirecting...');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handleDemoLogin = (type) => {
    if (type === 'admin') {
      setEmail('admin@sugandhi.com');
      setPassword('admin123');
      setLoginType('admin');
    } else {
      setEmail('staff@sugandhi.com');
      setPassword('staff123');
      setLoginType('staff');
    }
    // Auto submit after setting credentials
    setTimeout(() => {
      const formEvent = new Event('submit', { bubbles: true });
      const form = document.querySelector('form');
      if (form) form.dispatchEvent(formEvent);
    }, 100);
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      background: '#fff5f5'
    }}>
      {/* Left Side - Branding */}
      <div style={{
        flex: '1',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isMobile ? '40px 20px' : '40px',
        background: 'linear-gradient(135deg, #dc2626, #b91c1c)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Animated cherry blossom background */}
        <div style={{
          position: 'absolute',
          top: '-100px',
          right: '-100px',
          width: '400px',
          height: '400px',
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.1)',
          filter: 'blur(80px)',
          animation: 'pulse 8s ease-in-out infinite'
        }} />
        <div style={{
          position: 'absolute',
          bottom: '-100px',
          left: '-100px',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.08)',
          filter: 'blur(60px)',
          animation: 'pulse 10s ease-in-out infinite'
        }} />
        
        {/* Floating cherry blossoms */}
        <div style={{
          position: 'absolute',
          top: '10%',
          right: '10%',
          fontSize: '2rem',
          opacity: 0.15,
          animation: 'float 8s ease-in-out infinite'
        }}>🌸</div>
        <div style={{
          position: 'absolute',
          bottom: '15%',
          left: '5%',
          fontSize: '1.8rem',
          opacity: 0.12,
          animation: 'float 10s ease-in-out infinite reverse'
        }}>🌸</div>

        <div style={{ position: 'relative', zIndex: 1, textAlign: 'center', maxWidth: '500px' }}>
          {/* Logo */}
        
          <div style={{
            width: '210px',
            height: '170px',
            background: 'white',
            borderRadius: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
            padding: '15px'
          }}>
            <img 
              src="/Sugandhi Logo01.png" 
              alt="Sugandhi Flowers" 
              style={{ 
                width: '100%', 
                height: '100%', 
                objectFit: 'contain',
                display: 'block'
              }}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"%3E%3Crect width="100" height="100" fill="white"/%3E%3Ctext x="50" y="67" text-anchor="middle" fill="%23dc2626" font-size="50" font-weight="bold"%3ES%3C/text%3E%3C/svg%3E';
              }}
            />
          </div>
          

      
          <div style={{
            display: 'inline-block',
            padding: '6px 20px',
            background: 'rgba(255,255,255,0.2)',
            borderRadius: '20px',
            color: '#fff',
            fontWeight: 600,
            fontSize: '0.75rem',
            letterSpacing: '2px',
            marginBottom: '24px'
          }}>
            ADMIN PORTAL
          </div>

          <p style={{
            color: 'rgba(255,255,255,0.9)',
            fontSize: isMobile ? '0.85rem' : '0.95rem',
            lineHeight: 1.7,
            marginBottom: '40px'
          }}>
            Manage your entire flower shop from one place.
            Track orders, update products, and manage staff.
          </p>

          {/* Features Grid */}
          
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div style={{
        flex: '1',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isMobile ? '30px 20px' : '40px',
        background: 'white'
      }}>
        <div style={{ width: '100%', maxWidth: '420px' }}>
          {/* Login Type Toggle */}
          <div style={{
            display: 'flex',
            gap: '8px',
            marginBottom: '32px',
            background: '#fff5f5',
            borderRadius: '12px',
            padding: '4px',
            border: '1px solid rgba(220,38,38,0.2)'
          }}>
            {[
              { type: 'admin', label: 'Admin', icon: Shield },
              { type: 'staff', label: 'Staff', icon: User }
            ].map((item) => (
              <button
                key={item.type}
                onClick={() => {
                  setLoginType(item.type);
                  setEmail('');
                  setPassword('');
                }}
                style={{
                  flex: 1,
                  padding: isMobile ? '10px' : '12px',
                  borderRadius: '8px',
                  border: 'none',
                  background: loginType === item.type ? 'linear-gradient(135deg, #dc2626, #b91c1c)' : 'transparent',
                  color: loginType === item.type ? 'white' : '#64748b',
                  fontWeight: 600,
                  fontSize: isMobile ? '0.8rem' : '0.9rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'all 0.2s ease',
                  boxShadow: loginType === item.type ? '0 4px 12px rgba(220,38,38,0.3)' : 'none'
                }}
              >
                <item.icon size={isMobile ? 16 : 18} />
                {item.label}
              </button>
            ))}
          </div>

          <div style={{ marginBottom: '32px' }}>
            <h2 style={{
              fontSize: isMobile ? '1.3rem' : '1.6rem',
              fontWeight: 700,
              color: '#1a0a0a',
              marginBottom: '8px'
            }}>
              Welcome Back
            </h2>
            <p style={{ color: '#dc2626', fontSize: '0.85rem' }}>
              Sign in as {loginType === 'admin' ? 'Administrator' : 'Staff Member'}
            </p>
            <div style={{
              width: '50px',
              height: '2px',
              background: '#dc2626',
              marginTop: '12px',
              borderRadius: '2px'
            }} />
          </div>

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ ...labelStyle, color: '#dc2626' }}>
                <Mail size={16} /> Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={loginType === 'admin' ? 'admin@sugandhi.com' : 'staff@sugandhi.com'}
                style={inputStyle}
                onFocus={(e) => {
                  e.target.style.borderColor = '#dc2626';
                  e.target.style.boxShadow = '0 0 0 3px rgba(220,38,38,0.1)';
                  e.target.style.background = 'white';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e2e8f0';
                  e.target.style.boxShadow = 'none';
                  e.target.style.background = '#f8fafc';
                }}
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ ...labelStyle, color: '#dc2626' }}>
                <Lock size={16} /> Password
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  style={inputStyle}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#dc2626';
                    e.target.style.boxShadow = '0 0 0 3px rgba(220,38,38,0.1)';
                    e.target.style.background = 'white';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e2e8f0';
                    e.target.style.boxShadow = 'none';
                    e.target.style.background = '#f8fafc';
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    color: '#94a3b8',
                    cursor: 'pointer',
                    padding: '4px'
                  }}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: isMobile ? '12px' : '14px',
                background: loading ? '#94a3b8' : 'linear-gradient(135deg, #dc2626, #b91c1c)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: isMobile ? '0.9rem' : '1rem',
                fontWeight: 600,
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                transition: 'all 0.3s ease',
                boxShadow: loading ? 'none' : '0 4px 15px rgba(220,38,38,0.3)',
                opacity: loading ? 0.7 : 1
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 8px 25px rgba(220,38,38,0.4)';
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 15px rgba(220,38,38,0.3)';
                }
              }}
            >
              {loading ? (
                <>
                  <div className="spinner" style={{ borderTopColor: 'white' }} />
                  Signing in...
                </>
              ) : (
                <>
                  Sign In as {loginType === 'admin' ? 'Admin' : 'Staff'} <ArrowRight size={18} />
                </>
              )}
            </button>
          </form>

         

          
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 0.8; }
          50% { transform: scale(1.05); opacity: 1; }
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        
        .spinner {
          width: 18px;
          height: 18px;
          border: 2px solid rgba(255,255,255,0.3);
          border-top-color: white;
          borderRadius: '50%';
          animation: spin 0.6s linear infinite;
        }
        
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        
        @media (max-width: 768px) {
          input, button {
            font-size: 16px !important;
          }
        }
      `}</style>
    </div>
  );
};

const labelStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  fontSize: '0.85rem',
  fontWeight: 600,
  marginBottom: '6px'
};

const inputStyle = {
  width: '100%',
  padding: '12px 40px 12px 14px',
  border: '2px solid #e2e8f0',
  borderRadius: '10px',
  fontSize: '0.95rem',
  transition: 'all 0.2s ease',
  outline: 'none',
  background: '#f8fafc',
  fontFamily: 'inherit'
};

export default AdminLogin;